import type { CSSProperties } from 'react';

interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'rectangular' | 'circular';
  width?: string | number;
  height?: string | number;
  lines?: number;
}

export const Skeleton = ({
  className = '',
  variant = 'text',
  width,
  height,
  lines = 1,
}: SkeletonProps) => {
  const baseClasses = 'animate-pulse bg-neutral-200 rounded';

  const variantClasses = {
    text: 'h-4',
    rectangular: 'h-32',
    circular: 'rounded-full',
  };

  const style: CSSProperties = {
    width: width || '100%',
    height: height || undefined,
  };

  if (variant === 'text' && lines > 1) {
    return (
      <div className="space-y-2">
        {Array.from({ length: lines }).map((_, index) => (
          <div
            key={index}
            className={`${baseClasses} ${variantClasses.text} ${className}`}
            style={{
              ...style,
              width: index === lines - 1 ? '80%' : '100%',
            }}
          />
        ))}
      </div>
    );
  }

  return <div className={`${baseClasses} ${variantClasses[variant]} ${className}`} style={style} />;
};

const CardSkeleton = () => (
  <div className="space-y-4 rounded-lg border border-neutral-200 bg-white p-6">
    <div className="flex items-center justify-between">
      <Skeleton width="40%" height={24} />
      <Skeleton variant="rectangular" width={80} height={32} />
    </div>
    <Skeleton lines={3} />
    <div className="flex gap-2 pt-4">
      <Skeleton variant="rectangular" width={100} height={36} />
      <Skeleton variant="rectangular" width={100} height={36} />
    </div>
  </div>
);

const TableRowSkeleton = ({ columns = 5 }: { columns?: number }) => (
  <tr className="border-b border-neutral-200">
    {Array.from({ length: columns }).map((_, index) => (
      <td key={index} className="px-4 py-3">
        <Skeleton height={16} />
      </td>
    ))}
  </tr>
);

export const DocumentCardSkeleton = () => (
  <div className="space-y-3 rounded-lg border border-neutral-200 bg-white p-4">
    <div className="flex items-start justify-between">
      <div className="flex flex-1 items-center gap-3">
        <Skeleton variant="rectangular" width={40} height={40} className="rounded" />
        <div className="flex-1 space-y-2">
          <Skeleton width="70%" height={20} />
          <Skeleton width="40%" height={14} />
        </div>
      </div>
      <Skeleton variant="circular" width={32} height={32} />
    </div>
    <div className="flex gap-2">
      <Skeleton variant="rectangular" width={60} height={20} className="rounded-full" />
      <Skeleton variant="rectangular" width={80} height={20} className="rounded-full" />
    </div>
  </div>
);

export const TimelineItemSkeleton = () => (
  <div className="flex gap-4 pb-8">
    <div className="flex flex-col items-center">
      <Skeleton variant="circular" width={40} height={40} />
      <div className="mt-2 h-full w-0.5 animate-pulse bg-neutral-200" />
    </div>
    <div className="flex-1 space-y-3 pt-1">
      <Skeleton width="60%" height={20} />
      <Skeleton lines={2} />
      <div className="flex gap-2">
        <Skeleton variant="rectangular" width={80} height={24} className="rounded-full" />
        <Skeleton variant="rectangular" width={100} height={24} className="rounded-full" />
      </div>
    </div>
  </div>
);
